import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Divider, IconButton, MenuItem,
    Paper, Stack, TextField, Typography,
} from '@mui/material';
import { Add, ContentCopy, Delete, Save } from '@mui/icons-material';
import { getAppConfigs, updateAppConfig, createAppConfig } from '../../services/appConfigService';

/**
 *
 * Backing store = two AppConfig JSON keys (read by the backend with a 5-min cache):
 *   DEMAND_NUDGE_TEMPLATES        -> [{ id, name, title, body }]   ({city} interpolates to the ride's city)
 *   DEMAND_NUDGE_TEMPLATE_BY_CITY -> { [city]: templateId, default?: templateId }
 * A city with no assignment (and no `default`) gets the built-in English copy — deleting everything
 * here safely reverts to pre-template behavior.
 */

const TEMPLATES_KEY = 'DEMAND_NUDGE_TEMPLATES';
const BY_CITY_KEY = 'DEMAND_NUDGE_TEMPLATE_BY_CITY';
const BUILT_IN = '__builtin__'; // sentinel for "no template — built-in English copy"

export type NudgeTemplate = { id: string; name: string; title: string; body: string };
type Assignments = Record<string, string>;
type ConfigRow = { id: number; key: string; value: string };

const parseTemplates = (raw?: string): NudgeTemplate[] => {
    try {
        const v = JSON.parse(raw || '[]');
        return Array.isArray(v) ? v.filter(t => t && typeof t.id === 'string') : [];
    } catch { return []; }
};

const parseAssignments = (raw?: string): Assignments => {
    try {
        const v = JSON.parse(raw || '{}');
        return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    } catch { return {}; }
};

const newTemplateId = () => `tpl_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const interpolate = (s: string, city: string) => s.split('{city}').join(city);

interface Props {
    /** Launch-Mode cities — the only cities where the nudge fires, so the only ones worth assigning. */
    launchCities: string[];
}

const NudgeTemplatesPanel: React.FC<Props> = ({ launchCities }) => {
    const [rows, setRows] = useState<ConfigRow[]>([]);
    const [templates, setTemplates] = useState<NudgeTemplate[]>([]);
    const [assignments, setAssignments] = useState<Assignments>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState<number | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await getAppConfigs();
            const all: ConfigRow[] = Array.isArray(res.data) ? res.data : ((res.data as any).data || []);
            setRows(all);
            setTemplates(parseTemplates(all.find(r => r.key === TEMPLATES_KEY)?.value));
            setAssignments(parseAssignments(all.find(r => r.key === BY_CITY_KEY)?.value));
        } catch (e) { console.error('Failed to load nudge templates', e); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const saveKey = async (key: string, value: string) => {
        const existing = rows.find(r => r.key === key);
        if (existing) await updateAppConfig(existing.id, { value });
        else await createAppConfig({ key, value, description: 'Demand-nudge templates (set from City Operations)' });
    };

    const saveAll = async () => {
        setSaving(true);
        try {
            // Drop assignments pointing at deleted templates so the stored JSON never dangles.
            const ids = new Set(templates.map(t => t.id));
            const cleaned: Assignments = {};
            Object.entries(assignments).forEach(([city, id]) => { if (ids.has(id)) cleaned[city] = id; });
            await saveKey(TEMPLATES_KEY, JSON.stringify(templates));
            await saveKey(BY_CITY_KEY, JSON.stringify(cleaned));
            setAssignments(cleaned);
            setSavedAt(Date.now());
            await load();
        } catch (e: any) {
            alert(e.response?.data?.message || 'Failed to save nudge templates');
        } finally { setSaving(false); }
    };

    const updateTemplate = (id: string, patch: Partial<NudgeTemplate>) =>
        setTemplates(p => p.map(t => (t.id === id ? { ...t, ...patch } : t)));

    const removeTemplate = (id: string) => {
        setTemplates(p => p.filter(t => t.id !== id));
        setAssignments(p => {
            const next = { ...p };
            Object.keys(next).forEach(city => { if (next[city] === id) delete next[city]; });
            return next;
        });
    };

    const addTemplate = () =>
        setTemplates(p => [...p, {
            id: newTemplateId(),
            name: `Template ${p.length + 1}`,
            title: '🚖 Ride demand is high',
            body: 'Ride demand is high in {city} right now — go online to catch rides!',
        }]);

    // Clone = same wording under a new id, ready to tweak — no retyping. Assignments are NOT copied
    // (the clone starts unassigned; pick it in the city rows below).
    const cloneTemplate = (id: string) =>
        setTemplates(p => {
            const src = p.find(t => t.id === id);
            if (!src) return p;
            return [...p, { ...src, id: newTemplateId(), name: `${src.name} (copy)` }];
        });

    /** Cities currently using a template — shown on its card so reuse is visible at a glance. */
    const usedBy = (templateId: string): string[] =>
        Object.entries(assignments)
            .filter(([, id]) => id === templateId)
            .map(([city]) => (city === 'default' ? 'Default (all cities)' : city));

    const setAssignment = (city: string, templateId: string) =>
        setAssignments(p => {
            const next = { ...p };
            if (templateId === BUILT_IN) delete next[city];
            else next[city] = templateId;
            return next;
        });

    // `default` first, then each launch city.
    const assignmentRows = useMemo(() => ['default', ...launchCities], [launchCities]);

    if (loading) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

    return (
        <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>
                Nudge message templates (per city)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Templates are <strong>city-agnostic</strong>: write the wording once, put <code>{'{city}'}</code> where
                the city name should appear, and it fills in automatically per ride — so <strong>one template can be
                assigned to any number of cities</strong> (you only create another when you want different wording or
                a different language). Cities without an assignment use the <strong>Default</strong> row; if that's
                unset too, the built-in English message is sent. Changes apply within ~5 min (config cache).
            </Typography>

            {/* Template pool */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                {templates.map(t => (
                    <Paper key={t.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <TextField
                                size="small" label="Template name" value={t.name} sx={{ flex: 1 }}
                                onChange={e => updateTemplate(t.id, { name: e.target.value })}
                            />
                            <IconButton size="small" color="info" onClick={() => cloneTemplate(t.id)} title="Clone template (copy wording, then assign to another city)">
                                <ContentCopy fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => removeTemplate(t.id)} title="Delete template">
                                <Delete fontSize="small" />
                            </IconButton>
                        </Stack>
                        <TextField
                            fullWidth size="small" label="Notification title" value={t.title} sx={{ mb: 1 }}
                            onChange={e => updateTemplate(t.id, { title: e.target.value })}
                        />
                        <TextField
                            fullWidth size="small" label="Notification body" value={t.body} multiline minRows={2}
                            onChange={e => updateTemplate(t.id, { body: e.target.value })}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                            Preview ({launchCities[0] || 'Lucknow'}): <strong>{interpolate(t.title, launchCities[0] || 'Lucknow')}</strong>
                            {' — '}{interpolate(t.body, launchCities[0] || 'Lucknow')}
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }} alignItems="center">
                            <Typography variant="caption" color="textSecondary">Used by:</Typography>
                            {usedBy(t.id).length === 0
                                ? <Chip size="small" variant="outlined" label="nobody yet" />
                                : usedBy(t.id).map(c => <Chip key={c} size="small" color="success" variant="outlined" label={c} />)}
                        </Stack>
                    </Paper>
                ))}
            </Box>
            <Button size="small" startIcon={<Add />} onClick={addTemplate} sx={{ mb: 3 }}>
                Add template
            </Button>

            {/* Per-city assignment */}
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>
                City assignment
            </Typography>
            {launchCities.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    No Launch-Mode cities yet — only the Default assignment applies for now.
                </Typography>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
                {assignmentRows.map(city => (
                    <Stack key={city} direction="row" spacing={1} alignItems="center">
                        <Chip
                            size="small"
                            label={city === 'default' ? 'Default (all cities)' : city}
                            color={city === 'default' ? 'info' : 'success'}
                            sx={{ minWidth: 140 }}
                        />
                        <TextField
                            select fullWidth size="small"
                            value={assignments[city] && templates.some(t => t.id === assignments[city]) ? assignments[city] : BUILT_IN}
                            onChange={e => setAssignment(city, e.target.value)}
                        >
                            <MenuItem value={BUILT_IN}>
                                {city === 'default' ? 'Built-in English message' : 'Use Default row'}
                            </MenuItem>
                            {templates.map(t => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                ))}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
                <Button
                    variant="contained" color="secondary" size="small"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                    disabled={saving}
                    onClick={saveAll}
                >
                    Save templates & assignments
                </Button>
                {savedAt && <Typography variant="caption" color="success.main">Saved ✓</Typography>}
            </Stack>
        </Box>
    );
};

export default NudgeTemplatesPanel;
